// researchCheckin.ts — the time-based schedule for the longitudinal wellbeing
// check-in (battery: ONS-4 + GAD-7 + PHQ-8 + WSAS; GAD-7/PHQ-8 are research-only,
// never shown back to the user. The programme's own 6-item lives in-app per Act).
//
// Industry standard for outcome measurement is TIME-from-enrolment, not progress
// milestones: milestone triggers bias the sample toward fast completers and lose
// the people who stall — exactly the cohort an honest impact evaluation needs.
// Time anchoring gives every participant comparable measurement waves.
//
// Schedule (days from enrolment): baseline (0), 6 weeks (42), 12 weeks (84),
// 18 weeks (126), 24 weeks (168), then every 8 weeks (56 days) thereafter.
// Enrolment = onboarding.completedAt (when the participant actually starts).

import type { ProgressState } from './progress';
import type { MyProgressInstrument } from './sync';

const DAY = 86_400_000;
const FIXED_WINDOWS = [0, 42, 84, 126, 168]; // 0, 6, 12, 18, 24 weeks (days from enrolment)
const CADENCE_DAYS = 56; // then every 8 weeks (56 days) after week 24
/** Interval (days) from the previous submission to wave `w`: +6 weeks for waves
 *  1–4 (so they land at 6/12/18/24), then +8 weeks. */
function gapDaysTo(w: number): number { return w <= 4 ? 42 : CADENCE_DAYS; }
const parseMs = (s?: string | null): number => (s ? Date.parse(s) : NaN);
// Re-prompt cadence: an unfinished/skipped check-in re-surfaces every few days
// until it is actually RECORDED on the backend. Self-assessment is core to
// progress, so a skip/dismiss/closed-browser must not silence it for the window.
export const REPROMPT_DAYS = 3;

/** Day-from-enrolment that wave `i` opens (i = 0 is baseline). */
export function scheduleDay(i: number): number {
  if (i < FIXED_WINDOWS.length) return FIXED_WINDOWS[i];
  return FIXED_WINDOWS[FIXED_WINDOWS.length - 1] + (i - (FIXED_WINDOWS.length - 1)) * CADENCE_DAYS;
}

/**
 * How many check-in waves the server holds for this user — the ONE definition of
 * "submitted", shared by every screen that caches it.
 *
 * A wave row exists because a submission was recorded, so the count is the number
 * of rows, NOT the number of plottable scores. Home and Progress used to disagree
 * here (Progress counted only numeric composites) and wrote different numbers into
 * the same cached field, so a wave whose composite could not be scored read as
 * "not done" and the prompt came back on a user who had already submitted it.
 *
 * The fullest instrument wins: a wave the participant part-completed still counts.
 * Returns null when there is nothing to read from (offline, no pid, backend down) —
 * unknown is not zero, and callers must not prompt on it.
 */
export function recordedWaveCount(instruments: MyProgressInstrument[] | null | undefined): number | null {
  if (!instruments) return null;
  // fogs-v1 is the full FOGS check, not a wellbeing check-in wave: it must never advance the schedule.
  return instruments.filter((i) => i.id !== 'fogs-v1').reduce((m, i) => Math.max(m, i.waves?.length ?? 0), 0);
}

/** ISO timestamp of the most recent wave across all instruments — the anchor the
 *  rest of the schedule is measured from. undefined when nothing is recorded. */
export function lastWaveAt(instruments: MyProgressInstrument[] | null | undefined): string | undefined {
  if (!instruments) return undefined;
  return instruments
    .filter((i) => i.id !== 'fogs-v1')
    .flatMap((i) => i.waves || [])
    .map((w) => w.at)
    .filter((at): at is string => !!at)
    .sort()
    .pop();
}

export interface CheckinDue {
  due: boolean;
  /** Zero-based wave index (0 = baseline). */
  wave: number;
  /** Day-from-enrolment this window opened (e.g. 42). Used for the snooze key. */
  windowDay: number;
  /** True when this is the baseline wave — the check-in also collects demographics. */
  isBaseline: boolean;
}

/**
 * Decide whether a research check-in is due right now.
 *
 * A wave is DUE when its window has opened (enough time has passed since
 * enrolment) AND it has not yet been RECORDED on the backend. Completion is
 * backend-verified — `completedCount` is the number of check-in waves the server
 * holds for this user (from fetchMyProgress). Wave index `i` counts as recorded
 * once `completedCount > i`, so merely *opening* a check-in (or closing the
 * browser mid-form) never satisfies it.
 *
 * To avoid nagging, opening or dismissing the prompt sets `checkinSnoozeUntil`
 * (now + REPROMPT_DAYS); once that elapses, an still-unrecorded wave re-surfaces.
 * So a skipped baseline re-prompts every few days until it is actually done.
 *
 * Engagement floor: enrolment must exist (onboarding finished). The caller adds
 * the signed-in floor (the check-in needs a token to mint the pid).
 */
/**
 * The ms timestamp at which the next not-yet-recorded check-in wave opens.
 * With `completedCount` waves recorded, indices 0..completedCount-1 are done, so
 * the next wave is index `completedCount`. Returns null when there is no
 * enrolment date yet (we can't anchor the schedule).
 */
export function nextCheckinOpensAt(state: ProgressState, completedCount: number | null = 0): number | null {
  // Unknown (the server has never answered on this device) — we cannot place the
  // user on the schedule at all, so there is no next window to report.
  if (completedCount == null) return null;
  const enrolMs = parseMs(state.onboarding?.completedAt);
  // Baseline opens at enrolment.
  if (completedCount <= 0) return Number.isFinite(enrolMs) ? enrolMs : null;
  // Every later wave is timed from the LAST SUBMISSION date (state.lastResearchCheckinAt,
  // refreshed from the backend each fetch), so a late submission carries the whole
  // schedule forward from that date rather than from enrolment. Falls back to the
  // enrolment-anchored schedule for a user whose last submission date isn't known yet.
  const lastMs = parseMs(state.lastResearchCheckinAt);
  const anchor = Number.isFinite(lastMs)
    ? lastMs
    : (Number.isFinite(enrolMs) ? enrolMs + scheduleDay(completedCount - 1) * DAY : NaN);
  if (!Number.isFinite(anchor)) return null;
  return anchor + gapDaysTo(completedCount) * DAY;
}

/** Human "time remaining" for the next check-in, e.g. "3 weeks 2 days", "5 days",
 *  "tomorrow", "today". Rounds up so "in 1 day" reads as a day away, not zero. */
export function describeTimeUntil(targetMs: number, nowMs: number): string {
  const days = Math.ceil((targetMs - nowMs) / DAY);
  if (days <= 0) return 'today';
  if (days === 1) return 'tomorrow';
  const weeks = Math.floor(days / 7);
  const rem = days % 7;
  const wk = weeks ? `${weeks} week${weeks === 1 ? '' : 's'}` : '';
  const dy = rem ? `${rem} day${rem === 1 ? '' : 's'}` : '';
  return weeks && rem ? `${wk} ${dy}` : wk || dy;
}

export function researchCheckinDue(state: ProgressState, nowMs: number, completedCount: number | null = 0): CheckinDue {
  const none: CheckinDue = { due: false, wave: -1, windowDay: 0, isBaseline: false };
  // UNKNOWN count — the server has never told this device how many waves it holds
  // (fresh install, offline, backend cold-starting). A cached 0 and a never-fetched
  // 0 are indistinguishable, so guessing "baseline owed" puts "Complete a check-in"
  // in front of someone who has already done five. Say nothing until we know.
  // The count is cached once verified, so this only ever suppresses the card while
  // we are genuinely in the dark — and the check-in opens a web form that needs the
  // network anyway, so prompting offline would only lead to a page that cannot load.
  if (completedCount == null) return none;
  const snoozeMs = state.checkinSnoozeUntil ? Date.parse(state.checkinSnoozeUntil) : 0;
  const snoozed = Number.isFinite(snoozeMs) && snoozeMs > nowMs;

  // The BASELINE ("set your starting point") is offered to every signed-in user
  // straight away — including trial users before onboarding is finished — so
  // self-assessment is available from the first use. (The caller adds the signed-in floor.)
  if (completedCount <= 0) {
    return snoozed ? none : { due: true, wave: 0, windowDay: 0, isBaseline: true };
  }

  // Later waves: due once the interval from the LAST submission has elapsed. Completion
  // is backend-verified via completedCount, so indices 0..completedCount-1 are recorded
  // and the next wave is index completedCount.
  const opensAt = nextCheckinOpensAt(state, completedCount);
  if (opensAt == null || nowMs < opensAt || snoozed) return none;
  return { due: true, wave: completedCount, windowDay: scheduleDay(completedCount), isBaseline: false };
}
